import React, { useState, useEffect } from 'react';
import { ConsentNotice } from '@/components/ConsentNotice';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, User, Lock, Eye, EyeOff, Check, AlertCircle, UserCircle, Target, Loader2, Bell, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { BUSINESS_NAME } from '@/branding';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HEALTH_GOALS = [
  { id: 'longevity', label: 'Longevity', description: 'Living 90+ with Food Choices' },
  { id: 'wellness', label: 'Wellness', description: 'Right Balance of Food Nutrients' },
  { id: 'chronic_disease', label: 'Chronic Disease Care', description: 'Using Food to Manage Chronic Disease' },
  { id: 'weight_care', label: 'Weight Care', description: 'Using Food to Achieve a Healthy Size' },
  { id: 'elite_athlete', label: 'Elite Athlete Competitor', description: '' }
];

const UserProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, updateUser, logout } = useAuth();
  
  // Profile completion mode (from login redirect)
  const isProfileCompletion = location.state?.profileCompletion || !user?.profile_completed;
  
  // Name fields
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState('');
  
  // Health goals fields (for new user onboarding)
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [biggestBarrier, setBiggestBarrier] = useState('');
  const [profileStep, setProfileStep] = useState(1); // 1 = name, 2 = health goals
  
  // Password fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [analyticsConsent, setAnalyticsConsent] = useState(() => {
    try { return localStorage.getItem('insul_analytics_consent') === 'accepted'; } catch { return false; }
  });

  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deletionFallback, setDeletionFallback] = useState(null);

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toast.error('Please enter your password to confirm');
      return;
    }
    setDeleting(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/me/delete-account`,
        { password: deletePassword },
        { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.email_sent === false && res.data.cancel_url) {
        // Confirmation email failed - surface the cancellation link on screen so it isn't lost
        setDeletionFallback({ cancelUrl: res.data.cancel_url, purgeDate: res.data.permanent_erasure_on });
        setShowDeleteConfirm(false);
      } else {
        toast.success(`Account deactivated. Permanent erasure on ${res.data.permanent_erasure_on}. Check your email to cancel anytime before then.`, { duration: 10000 });
        logout();
        navigate('/login');
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  const handleCopyCancelLink = async () => {
    try {
      await navigator.clipboard.writeText(deletionFallback.cancelUrl);
      toast.success('Cancellation link copied');
    } catch {
      toast.error('Copy failed - select and copy the link manually');
    }
  };

  useEffect(() => {
    const loadConsents = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/auth/me/consents`, { headers: { Authorization: `Bearer ${token}` } });
        setMarketingOptIn(res.data.compliance_consents?.marketing_consent?.value === true);
      } catch (e) { /* default false */ }
    };
    if (token) loadConsents();
  }, [token]);

  const handleMarketingToggle = async (checked) => {
    setMarketingOptIn(checked);
    try {
      await axios.post(`${API_URL}/api/auth/me/consents`, { marketing_consent: checked, screen: 'profile_privacy', notice_key: 'marketing_checkbox' },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(checked ? 'Reminder emails enabled' : 'Unsubscribed from reminder emails');
    } catch (e) {
      setMarketingOptIn(!checked);
      toast.error('Failed to update preference');
    }
  };

  const handleExportMealLog = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API_URL}/api/auth/me/export/meal-log`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `insul-meal-log-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Meal log downloaded');
    } catch (e) {
      toast.error('Failed to export meal log');
    } finally {
      setExporting(false);
    }
  };

  const handleAnalyticsToggle = (checked) => {
    try { localStorage.setItem('insul_analytics_consent', checked ? 'accepted' : 'rejected'); } catch {}
    setAnalyticsConsent(checked);
    if (checked) {
      window.__insulInitAnalytics?.();
      window.posthog?.opt_in_capturing?.();
      toast.success('Optional analytics enabled');
    } else {
      window.posthog?.opt_out_capturing?.();
      window.posthog?.stopSessionRecording?.();
      toast.success('Optional analytics disabled');
    }
  };
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Email preferences
  const [emailRemindersEnabled, setEmailRemindersEnabled] = useState(false);
  const [loadingPreferences, setLoadingPreferences] = useState(true);
  const [savingPreferences, setSavingPreferences] = useState(false);

  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
    }
  }, [user]);

  // Fetch email preferences
  useEffect(() => {
    const fetchEmailPreferences = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API_URL}/api/auth/email-preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEmailRemindersEnabled(response.data.email_reminders_enabled);
      } catch (error) {
        console.error('Failed to fetch email preferences:', error);
      } finally {
        setLoadingPreferences(false);
      }
    };
    fetchEmailPreferences();
  }, [token]);

  const handleEmailRemindersToggle = async (enabled) => {
    setSavingPreferences(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/email-preferences`,
        { email_reminders_enabled: enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmailRemindersEnabled(enabled);
      toast.success(enabled ? 'Daily reminders enabled' : 'Daily reminders disabled');
    } catch (error) {
      toast.error('Failed to update preferences');
    } finally {
      setSavingPreferences(false);
    }
  };

  const toggleGoal = (goalId) => {
    setSelectedGoals(prev => 
      prev.includes(goalId) 
        ? prev.filter(id => id !== goalId)
        : [...prev, goalId]
    );
  };

  const handleNameSubmit = (e) => {
    e.preventDefault();
    setProfileError('');

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();

    if (!trimmedFirst || !trimmedLast) {
      setProfileError('Please enter both first and last name');
      return;
    }

    if (trimmedFirst.length < 2 || trimmedLast.length < 2) {
      setProfileError('Names must be at least 2 characters');
      return;
    }

    // Move to health goals step
    setProfileStep(2);
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileError('');

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();

    if (!trimmedFirst || !trimmedLast) {
      setProfileError('Please enter both first and last name');
      return;
    }

    if (selectedGoals.length === 0) {
      setProfileError('Please select at least one health goal');
      return;
    }

    setSavingProfile(true);
    try {
      // Save profile
      const response = await axios.post(
        `${API_URL}/api/auth/complete-profile`,
        {
          first_name: trimmedFirst,
          last_name: trimmedLast,
          health_goals: selectedGoals,
          biggest_barrier: biggestBarrier.trim()
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Send health goals email to the business mailbox (support@insulnutrients.com; backend reads ADMIN_NOTIFY_EMAIL)
      try {
        await axios.post(`${API_URL}/api/new-user-health-goals`, {
          user_name: `${trimmedFirst} ${trimmedLast}`,
          user_email: user.email,
          health_goals: selectedGoals.map(id => {
            const goal = HEALTH_GOALS.find(g => g.id === id);
            return goal ? `${goal.label}${goal.description ? ` (${goal.description})` : ''}` : id;
          }),
          biggest_barrier: biggestBarrier.trim()
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (emailError) {
        console.error('Failed to send health goals email:', emailError);
        // Don't block the user if email fails
      }
      
      // Update the user context
      if (updateUser) {
        updateUser({
          ...user,
          first_name: trimmedFirst,
          last_name: trimmedLast,
          profile_completed: true
        });
      }
      
      toast.success('Profile completed successfully!');
      
      // Redirect to dashboard
      setTimeout(() => navigate('/dashboard'), 1000);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to save profile';
      setProfileError(detail);
      toast.error(detail);
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to change password';
      setError(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  // Profile completion mode - simplified UI with health goals
  if (isProfileCompletion && !user?.profile_completed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-transparent flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-lg"
        >
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-6">
              <span className="text-3xl font-bold uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>INSUL NUTRIENTS</span>
              <span className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{BUSINESS_NAME}</span>
            </div>
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
              {profileStep === 1 ? (
                <UserCircle className="w-12 h-12 text-primary" />
              ) : (
                <Target className="w-12 h-12 text-primary" />
              )}
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {profileStep === 1 ? 'Complete Your Profile' : 'Your Health Goals'}
            </h1>
            <p className="text-muted-foreground">
              {profileStep === 1 ? 'Please enter your name to continue' : 'Help us understand your nutrition journey'}
            </p>
            
            {/* Step indicator */}
            <div className="flex items-center justify-center gap-2 mt-4">
              <div className={`w-3 h-3 rounded-full ${profileStep >= 1 ? 'bg-primary' : 'bg-gray-300'}`} />
              <div className={`w-8 h-1 ${profileStep >= 2 ? 'bg-primary' : 'bg-gray-300'}`} />
              <div className={`w-3 h-3 rounded-full ${profileStep >= 2 ? 'bg-primary' : 'bg-gray-300'}`} />
            </div>
          </div>

          <div className="bg-card rounded-2xl border border-border/50 shadow-lg p-6">
            {profileStep === 1 ? (
              /* Step 1: Name Entry */
              <form onSubmit={handleNameSubmit} className="space-y-4">
                {profileError && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-sm text-red-700">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {profileError}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-sm font-medium">
                    First Name *
                  </Label>
                  <Input
                    id="firstName"
                    data-testid="first-name-input"
                    type="text"
                    placeholder="Enter your first name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="rounded-xl"
                    autoFocus
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-sm font-medium">
                    Last Name *
                  </Label>
                  <Input
                    id="lastName"
                    data-testid="last-name-input"
                    type="text"
                    placeholder="Enter your last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="rounded-xl"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="continue-button"
                  disabled={!firstName.trim() || !lastName.trim()}
                  className="w-full rounded-xl bg-primary hover:bg-primary/90 text-white font-semibold py-3"
                >
                  Continue
                  <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                </Button>
              </form>
            ) : (
              /* Step 2: Health Goals */
              <form onSubmit={handleProfileSave} className="space-y-6">
                {profileError && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-sm text-red-700">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {profileError}
                  </div>
                )}

                {/* Health Goals Selection */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">
                    What is your Health Goal? *
                    <span className="text-muted-foreground font-normal ml-1">(Select all that apply)</span>
                  </Label>
                  <div className="space-y-2">
                    {HEALTH_GOALS.map((goal) => (
                      <button
                        key={goal.id}
                        type="button"
                        onClick={() => toggleGoal(goal.id)}
                        data-testid={`goal-${goal.id}`}
                        className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                          selectedGoals.includes(goal.id)
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${
                            selectedGoals.includes(goal.id)
                              ? 'border-primary bg-primary'
                              : 'border-gray-300'
                          }`}>
                            {selectedGoals.includes(goal.id) && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{goal.label}</p>
                            {goal.description && (
                              <p className="text-sm text-muted-foreground">{goal.description}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Biggest Barrier */}
                <div className="space-y-2">
                  <Label htmlFor="barrier" className="text-sm font-medium">
                    What is the single biggest barrier to date to achieving that Health Goal?
                  </Label>
                  <Textarea
                    id="barrier"
                    data-testid="barrier-input"
                    placeholder="Write a single sentence describing your biggest challenge..."
                    value={biggestBarrier}
                    onChange={(e) => setBiggestBarrier(e.target.value)}
                    className="rounded-xl resize-none"
                    rows={3}
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setProfileStep(1)}
                    className="rounded-xl"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    type="submit"
                    data-testid="save-profile-button"
                    disabled={savingProfile || selectedGoals.length === 0}
                    className="flex-1 rounded-xl bg-primary hover:bg-primary/90 text-white font-semibold py-3"
                  >
                    {savingProfile ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Complete Profile
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  // Full profile page
  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-transparent">
      {/* Header */}
      <nav className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>INSUL NUTRIENTS</span>
            <span className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{BUSINESS_NAME}</span>
          </div>
          <Button
            data-testid="back-to-dashboard-button"
            variant="outline"
            className="rounded-full px-4 py-2 font-medium"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Page Title */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              User Profile
            </h1>
            <p className="text-muted-foreground">{user.email}</p>
          </div>

          {/* Profile Card */}
          <div className="bg-card rounded-2xl border border-border/50 shadow-lg overflow-hidden">
            {/* Personal Information Section */}
            <div className="p-6 border-b border-border/50">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <UserCircle className="w-5 h-5 text-primary" />
                Personal Information
              </h2>
              <form onSubmit={handleProfileSave} className="space-y-4">
                {profileError && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-sm text-red-700">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {profileError}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-sm font-medium">
                      First Name
                    </Label>
                    <Input
                      id="firstName"
                      data-testid="first-name-input"
                      type="text"
                      placeholder="Enter first name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-sm font-medium">
                      Last Name
                    </Label>
                    <Input
                      id="lastName"
                      data-testid="last-name-input"
                      type="text"
                      placeholder="Enter last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="rounded-xl"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="save-profile-button"
                  disabled={savingProfile}
                  className="rounded-xl"
                >
                  {savingProfile ? 'Saving...' : 'Save Changes'}
                </Button>
              </form>
            </div>

            {/* Account Info Section */}
            <div className="p-6 border-b border-border/50">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Account Information
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-muted-foreground">Email</span>
                  <span className="font-medium">{user.email}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-muted-foreground">Account Type</span>
                  <span className="font-medium">
                    {user.is_admin ? 'Admin' : 'User'}
                  </span>
                </div>
                {user.first_name && user.last_name && (
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-muted-foreground">Full Name</span>
                    <span className="font-medium">{user.first_name} {user.last_name}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Notification Preferences Section */}
            <div className="p-6 border-b border-border/50">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Bell className="w-5 h-5 text-primary" />
                Notification Preferences
              </h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium">Daily Meal Reminders</p>
                    <p className="text-sm text-muted-foreground">
                      Receive a daily email reminder to log your meals
                    </p>
                  </div>
                  {loadingPreferences ? (
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  ) : (
                    <Switch
                      data-testid="email-reminders-toggle"
                      checked={emailRemindersEnabled}
                      onCheckedChange={handleEmailRemindersToggle}
                      disabled={savingPreferences}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Privacy & Analytics Section */}
            <div className="p-6 border-b border-border/50">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5 text-primary" />
                Privacy &amp; Analytics
              </h2>
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium">Optional analytics &amp; session replay</p>
                  <p className="text-sm text-muted-foreground">
                    Help us improve the app by sharing anonymous usage data (PostHog). The app works
                    fully without it. You can change this anytime.
                  </p>
                </div>
                <Switch
                  data-testid="analytics-consent-toggle"
                  checked={analyticsConsent}
                  onCheckedChange={handleAnalyticsToggle}
                />
              </div>
              <div className="flex items-center justify-between py-2 border-t border-border/40 mt-2 pt-4">
                <div>
                  <p className="font-medium">Daily reminder emails</p>
                  <p className="text-sm text-muted-foreground">
                    <ConsentNotice slot="marketing_checkbox">Optional meal-logging reminders. Account and security emails (password resets,
                    confirmations) are always sent regardless of this setting.</ConsentNotice>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1"><ConsentNotice slot="withdrawal">{null}</ConsentNotice></p>
                </div>
                <Switch
                  data-testid="marketing-consent-toggle"
                  checked={marketingOptIn}
                  onCheckedChange={handleMarketingToggle}
                />
              </div>
              <div className="flex items-center justify-between py-2 border-t border-border/40 mt-2 pt-4">
                <div>
                  <p className="font-medium">Download my meal log</p>
                  <p className="text-sm text-muted-foreground">
                    CSV of your meals and drinks with macro and micronutrient estimates, date and meal type.
                  </p>
                </div>
                <Button
                  data-testid="export-meal-log-btn"
                  onClick={handleExportMealLog}
                  disabled={exporting}
                  variant="outline"
                  className="rounded-full"
                >
                  {exporting ? 'Preparing…' : 'Download CSV'}
                </Button>
              </div>
            </div>

            {/* Change Password Section */}
            <div className="p-6 border-b border-border/50">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Lock className="w-5 h-5 text-primary" />
                Change Password
              </h2>
              
              <form onSubmit={handlePasswordChange} className="space-y-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-sm text-red-700">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="currentPassword" className="text-sm font-medium">
                    Current Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      data-testid="current-password-input"
                      type={showCurrentPassword ? 'text' : 'password'}
                      placeholder="Enter current password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="rounded-xl pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-sm font-medium">
                    New Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      data-testid="new-password-input"
                      type={showNewPassword ? 'text' : 'password'}
                      placeholder="Enter new password (min 8 characters)"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="rounded-xl pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium">
                    Confirm New Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      data-testid="confirm-password-input"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="rounded-xl pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="change-password-button"
                  disabled={loading || !currentPassword || !newPassword || !confirmPassword}
                  className="w-full rounded-xl bg-primary hover:bg-primary/90"
                >
                  {loading ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Changing Password...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Change Password
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Delete Account Section */}
            <div className="p-6 bg-red-50/40">
              <h2 className="text-lg font-semibold mb-2 flex items-center gap-2 text-red-700">
                <Trash2 className="w-5 h-5" />
                Delete Account
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                Deactivates your account immediately. All of your data is permanently and irreversibly
                erased after a 30-day grace period. You can cancel anytime during those 30 days by
                logging in again or using the link in the confirmation email.
              </p>
              {deletionFallback && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4" data-testid="deletion-fallback-overlay">
                  <div className="bg-white rounded-2xl border border-amber-300 shadow-2xl max-w-lg w-full p-6">
                    <h3 className="text-lg font-bold text-amber-700 flex items-center gap-2 mb-2">
                      <AlertCircle className="w-5 h-5" />
                      Save your cancellation link
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Your account is deactivated and will be <strong>permanently erased on {deletionFallback.purgeDate}</strong>.
                      We could not send your confirmation email, so this link is your record — <strong>save it now</strong>.
                      Opening it any time before that date cancels the deletion. You can also cancel by signing in
                      with your email and password and choosing "Restore My Account".
                    </p>
                    <div className="bg-slate-100 border border-border rounded-xl p-3 text-xs break-all select-all mb-4" data-testid="deletion-cancel-link">
                      {deletionFallback.cancelUrl}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                        onClick={handleCopyCancelLink}
                        variant="outline"
                        className="rounded-full"
                        data-testid="copy-cancel-link-button"
                      >
                        Copy link
                      </Button>
                      <Button
                        onClick={() => { logout(); navigate('/login'); }}
                        className="rounded-full bg-red-600 hover:bg-red-700 text-white"
                        data-testid="deletion-fallback-signout-button"
                      >
                        I've saved the link — sign me out
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              {!showDeleteConfirm ? (
                <Button
                  variant="outline"
                  data-testid="delete-account-button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="rounded-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete my account
                </Button>
              ) : (
                <div className="bg-white border border-red-200 rounded-xl p-4 space-y-3" data-testid="delete-account-confirm-panel">
                  <p className="text-sm text-red-700 font-medium flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    Enter your password to confirm. Your account will be deactivated immediately.
                  </p>
                  <Input
                    type="password"
                    data-testid="delete-account-password-input"
                    placeholder="Your password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    className="rounded-xl"
                  />
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      data-testid="delete-account-confirm-button"
                      onClick={handleDeleteAccount}
                      disabled={deleting || !deletePassword}
                      className="rounded-full bg-red-600 hover:bg-red-700 text-white"
                    >
                      {deleting ? 'Deleting...' : 'Permanently delete in 30 days'}
                    </Button>
                    <Button
                      variant="outline"
                      data-testid="delete-account-cancel-button"
                      onClick={() => { setShowDeleteConfirm(false); setDeletePassword(''); }}
                      className="rounded-full"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default UserProfilePage;
